import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import firebaseInit from '@/firebaseInit.js'
import {USER_IDS} from '@/userAccessLevel'
import {clearAdminPowerSync} from '@/utils/adminPowerSync'
import {signupBody, profileBody, signupError} from '@/utils/userProfile'
import {createTokenSource, installAuthInterceptors, refreshOnReturn} from '@/utils/authToken'
import {findSavedExhibitorByName} from '@/utils/exhibitorMatch'
import {lookupUserAccount} from '@/utils/userAccountLookup'
import event from './modules/event'
import entryGroup from './modules/entryGroup'
import admin from './modules/admin'
import showSeries_ from './modules/showSeries'
import organization_ from './modules/organization'
// Removed Log Rocket because it was basically useless and slowing the site down I thought.

import LogRocket from 'logrocket'
import createPlugin from 'logrocket-vuex'
const logrocketPlugin = createPlugin(LogRocket)

/* if (process.env.NODE_ENV !== 'development') {
  LogRocket.init('kzaivo/showman')
} */

var auth = firebaseInit.auth

Vue.use(Vuex)
const store = new Vuex.Store({
  plugins: [logrocketPlugin],
  modules: {
    event,
    entryGroup,
    admin,
    showSeries_,
    organization_
  },
  state: {
    // Authentication Set up

    loading: false,
    appInitiated: false,
    authInitiated: false,
    apiInstance: axios.create({
      // baseURL: 'http://localhost:5000',
      baseURL: process.env.VUE_APP_ROOT_API, // 'https://showman-db.herokuapp.com/',
      timeout: 40000
    }),
    // The Rails API (showman-api). VUE_APP_RAILS_API lets a containerized worktree point
    // at its own published Rails port; otherwise the laptop / production defaults.
    railsAPI: axios.create({
      baseURL: process.env.VUE_APP_RAILS_API || (process.env.NODE_ENV === 'development'
        ? 'http://localhost:3000/'
        : 'https://showman-api-0e24b17f8ff6.herokuapp.com/')
    }),
    authorizationSet: false,
    user: JSON.parse(localStorage.getItem('currentUser')), // auth.currentUser,
    userData: JSON.parse(localStorage.getItem('userData')),

    // Impersonation state
    isImpersonating: false,
    impersonatedUid: null,
    originalAdminUser: null,
    originalAdminUserData: null,
    // Who may START impersonation from the UI. Deliberately narrower than the
    // backend, which accepts the Showman-Impersonate-Uid header from anyone in
    // SUPPORT_UIDS (showman-db-v1/src/utils/internalSupport.js).
    //
    // BEFORE OPENING THIS UP TO THE TEAM, address the known gaps:
    //   - writes are attributed to the impersonated user; realUid is only in logs
    //   - impersonation never expires, and survives in localStorage
    //   - railsAPI and direct Firebase calls still run as the support account
    //   - SUPPORT_UIDS and the DB `role = ADMIN` list disagree about who is staff
    superAdminUserIds: [
      USER_IDS.JOSH,
      USER_IDS.JASON
    ],

    // General Application
    upcomingEvents: [],
    recentEvents: [],
    allEvents: [],
    showSeries: [],
    judges: [],
    classes: {},
    eventEvents: [],
    showTypes: [],
    stripeSession: {},
    searchParams: {
      location: null,
      radius: 50,
      distanceType: 'radius', // 'radius' or 'drivingTime'
      drivingTime: 30,
      date: null,
      startDate: null,
      endDate: null,
      genre: null,
      artist: null,
      species: [],
      name: null,
      btrjla: null
    },
    mapBounds: null, // Store current map bounds for dynamic filtering
    showMap: true, // Map visibility state
    archiveSearchParams: {
      state: null,
      species: null,
      name: null,
      year: null
    }
  },
  mutations: {
    authInitiated: state => {
      state.authInitiated = true
    },
    appInitiated: state => {
      state.appInitiated = true
    },
    // User Mutations
    setUser: (state, payload) => {
      if (payload.user) {
        if (payload.user.address) {
          if (!payload.user.address.country) {
            payload.user.address.country = 'US'
          }
        }
      }
      state.user = payload.user
      localStorage.setItem('currentUser', JSON.stringify(state.user))
      if (state.user) {
        if (state.user.user_id) {
          /* LogRocket.identify(state.user.user_id, {
            email: state.user.email,
            name: `${state.user.first_name} ${state.user.last_name}`
          }) */
        }
      } else {
      }
    },
    setStripeSession: (state, payload) => {
      state.stripeSession = payload
    },
    setUserField: (state, payload) => {
      state.user[payload.field] = payload.value
    },
    setAuthHeaders: (state, payload) => {
      if (state.apiInstance) {
        state.apiInstance.defaults.headers.common.Authorization = payload.token
      }

      if (state.railsAPI) {
        state.railsAPI.defaults.headers.common['Firebase-Token'] = payload.token
      }
    },
    // While this header is set the API treats the request as coming from the
    // impersonated user - same uid, same permissions they really have. Without it
    // the backend only ever sees the support account and every gate passes.
    setImpersonationHeader: (state, uid) => {
      if (!state.apiInstance) return
      if (uid) {
        state.apiInstance.defaults.headers.common['Showman-Impersonate-Uid'] = uid
      } else {
        delete state.apiInstance.defaults.headers.common['Showman-Impersonate-Uid']
      }
    },
    authorizationSet: (state) => {
      state.authorizationSet = true
    },
    setUserData: (state, payload) => {
      state.userData = payload.userData
      localStorage.setItem('userData', JSON.stringify(state.userData))
    },
    setUpcomingEvents: (state, payload) => {
      state.upcomingEvents = payload.events
    },
    setRecentEvents: (state, payload) => {
      state.recentEvents = payload.events
    },
    setAllEvents: (state, payload) => {
      state.allEvents = payload.events
    },
    setShowSeries: (state, payload) => {
      state.showSeries = payload.events
    },
    addJudgeToList: (state, payload) => {
      state.judges.push(payload)
    },
    updateJudgeList: (state, payload) => {
      state.judges.forEach(j => {
        if (j.judge_id === payload.judge_id) {
          j = payload
        }
      })
    },
    setEventEvents: (state, payload) => {
      state.eventEvents = payload.events
    },
    setShowTypes: (state, payload) => {
      state.showTypes = payload.showTypes
    },
    addUserAnimalList: (state, animal) => {
      animal.status = 'ACTIVE'
      state.userData.animals.push(animal)
    },
    updateUserAnimalList: (state, animal) => {
      state.userData.animals.forEach(a => {
        if (a.animal_id === animal.animal_id) {
          Vue.set(a, 'animal_details', animal.animal_details)
        }
      })
    },
    // Commented Out on 4/28
    addToExhibitorList: (state, exhibitor) => {
      state.userData.exhibitors.push(exhibitor)
    },
    updateExhibitorInList: (state, exhibitor) => {
      state.userData.exhibitors.forEach(e => {
        if (e.exhibitor_id === exhibitor.exhibitor_id) {
          Vue.set(e, 'entry_details', exhibitor.exhibitor_details)
        }
      })
    },
    // Generic User Mutators
    userDataAddData: (state, payload) => {
      state.userData[payload.name].push(payload)
    },
    userDataRemoveNewData: (state, payload) => {
      // Remove last item if it didn't save
      state.userData[payload.name].pop()
    },
    userDataSetNewDataID: (state, payload) => {
      state.userData[payload.name][state.userData[payload.name].length - 1][payload.idName] = payload.id
    },
    userDataUpdateData: (state, payload) => {
      state.userData[payload.name].forEach(d => {
        if (d[payload.idName] === payload[payload.idName]) {
          d = payload
        }
      })
    },
    userDataRemoveData: (state, payload) => {
      state.userData[payload.name] = state.userData[payload.name].filter(d => {
        if (d[payload.idName] !== payload[payload.idName]) {
          return d
        }
      })
    },
    userDataAddData_addon: (state, payload) => {
      state.userData.show_series_membership.forEach(m => {
        if (m.show_series_id === payload.show_series_id && m.exhibitor_id === payload.exhibitor_id) {
          m.add_ons.push(payload)
        }
      })
    },
    userData_removeAddon: (state, payload) => {
      state.userData.show_series_membership.forEach(m => {
        if (m.show_series_id === payload.show_series_id && m.exhibitor_id === payload.exhibitor_id) {
          m.add_ons = m.add_ons.filter(a => {
            return payload.show_series_member_add_on_id !== a.show_series_member_add_on_id
          })
        }
      })
    },
    userDataAddData_animalNomination: (state, payload) => {
      state.userData.show_series_membership.forEach(m => {
        if (m.show_series_id === payload.show_series_id && m.exhibitor_id === payload.exhibitor_id) {
          m.animal_nominations.push(payload)
        }
      })
    },
    userDataUpdateData_animalNomination: (state, payload) => {
      state.userData.show_series_membership.forEach(m => {
        if (m.show_series_id === payload.show_series_id && m.exhibitor_id === payload.exhibitor_id) {
          m.animal_nominations.forEach(an => {
            if (an.show_series_animal_nomination_id === payload.show_series_animal_nomination_id) {
              an.nomination.details = payload.nomination.details
            }
          })
        }
      })
    },
    userData_removeAnimalNomination: (state, payload) => {
      state.userData.show_series_membership.forEach(m => {
        if (m.show_series_id === payload.show_series_id && m.exhibitor_id === payload.exhibitor_id) {
          m.animal_nominations = m.animal_nominations.filter(a => {
            return payload.show_series_animal_nomination_id !== a.show_series_animal_nomination_id
          })
        }
      })
    },
    userData_removeMember: (state, payload) => {
      state.userData.show_series_membership = state.userData.show_series_membership.filter(m => {
        return m.id !== payload.id
      })
    },
    userData_removeSanctioningForm: (state, payload) => {
      state.userData.series_sanctioning = state.userData.series_sanctioning.filter(s => {
        return s.id !== payload.id
      })
    },
    userData_updateSanctioningForm: (state, payload) => {
      state.userData.series_sanctioning.forEach((s, i) => {
        if (s.id === payload.id) {
          Vue.set(state.userData.series_sanctioning, i, payload)
        }
      })
    },
    setSearchParam: (state, payload) => {
      state.searchParams[payload.field] = payload.value
    },
    setArchiveSearchParam: (state, payload) => {
      state.archiveSearchParams[payload.field] = payload.value
    },
    setMapBounds: (state, payload) => {
      state.mapBounds = payload
    },
    removeShowTemplate: (state, payload) => {
      state.showSeries = state.showSeries.filter(s => {
        return s.show_template_id !== payload.show_template_id
      })
    },
    setDistanceType: (state, distanceType) => {
      state.searchParams.distanceType = distanceType
    },
    setDrivingTime: (state, drivingTime) => {
      state.searchParams.drivingTime = drivingTime
    },
    setSpecies: (state, species) => {
      state.searchParams.species = species
    },
    setLocation: (state, location) => {
      state.searchParams.location = location
    },
    setRadius: (state, radius) => {
      state.searchParams.radius = radius
    },
    setStartDate: (state, startDate) => {
      state.searchParams.startDate = startDate
    },
    setEndDate: (state, endDate) => {
      state.searchParams.endDate = endDate
    },
    setName: (state, name) => {
      state.searchParams.name = name
    },
    logUserIn: (state, payload) => {
      state.user = payload
      state.auth_loaded = true
    },
    logUserOut: (state) => {
      state.user = null
      state.userData = null
    },
    setShowMap: (state, payload) => {
      state.showMap = payload
    },
    // Impersonation mutations
    startImpersonation: (state, payload) => {
      // Store original admin data
      state.originalAdminUser = state.user
      state.originalAdminUserData = state.userData
      state.isImpersonating = true
      state.impersonatedUid = payload.user.user_id

      // Set impersonated user data
      state.user = payload.user
      state.userData = payload.userData

      // Update localStorage with impersonated user
      localStorage.setItem('currentUser', JSON.stringify(state.user))
      localStorage.setItem('userData', JSON.stringify(state.userData))
      localStorage.setItem('isImpersonating', 'true')
      localStorage.setItem('impersonatedUid', state.impersonatedUid)
      localStorage.setItem('originalAdminUser', JSON.stringify(state.originalAdminUser))
      localStorage.setItem('originalAdminUserData', JSON.stringify(state.originalAdminUserData))
    },
    stopImpersonation: (state) => {
      if (state.isImpersonating && state.originalAdminUser) {
        // Restore original admin data
        state.user = state.originalAdminUser
        state.userData = state.originalAdminUserData
        state.isImpersonating = false
        state.impersonatedUid = null

        // Clear impersonation data
        state.originalAdminUser = null
        state.originalAdminUserData = null

        // Update localStorage with original admin data
        localStorage.setItem('currentUser', JSON.stringify(state.user))
        localStorage.setItem('userData', JSON.stringify(state.userData))
        localStorage.removeItem('isImpersonating')
        localStorage.removeItem('impersonatedUid')
        localStorage.removeItem('originalAdminUser')
        localStorage.removeItem('originalAdminUserData')
      }
    },
    // Unconditional teardown, used on sign-out. stopImpersonation only acts when the
    // original admin is still in state; this always leaves a clean slate so a stale
    // flag can't make the next sign-in restore someone else's session.
    clearImpersonation: (state) => {
      state.isImpersonating = false
      state.impersonatedUid = null
      state.originalAdminUser = null
      state.originalAdminUserData = null
      localStorage.removeItem('isImpersonating')
      localStorage.removeItem('impersonatedUid')
      localStorage.removeItem('originalAdminUser')
      localStorage.removeItem('originalAdminUserData')
    },
    restoreImpersonationFromStorage: (state) => {
      const isImpersonating = localStorage.getItem('isImpersonating') === 'true'
      if (isImpersonating) {
        state.isImpersonating = true
        state.originalAdminUser = JSON.parse(localStorage.getItem('originalAdminUser'))
        state.originalAdminUserData = JSON.parse(localStorage.getItem('originalAdminUserData'))
        // Fall back to the stored user for sessions that began before this was tracked
        state.impersonatedUid = localStorage.getItem('impersonatedUid') ||
          (state.user && state.user.user_id) || null
      }
    }
  },
  actions: {
    // User Actions
    authChange: ({commit, dispatch}, payload) => {
      // logUserOutcommit('setUser', payload)
      if (payload.user) {
        payload.user.getIdToken(true)
          .then(token => {
            commit('setAuthHeaders', {token: token})
            commit('authInitiated')
            commit('authorizationSet')
            // Restore impersonation before loading user data: if a session is
            // impersonating, we want that user's data, not the signed-in account's.
            return dispatch('initializeImpersonationState')
              .then(impersonatedUid => {
                return dispatch('loadUserData', {uid: impersonatedUid || payload.user.uid})
              })
          })
      } else {
        commit('clearImpersonation')
        commit('setImpersonationHeader', null)
        commit('setUser', {user: null})
        commit('setUserData', {userData: null})
        commit('setAuthHeaders', {token: ''})
        commit('authInitiated')
      }
    },
    createUser: ({state, commit, dispatch}, payload) => {
      var user = null
      return auth.createUserWithEmailAndPassword(payload.email, payload.password)
        .then((userDoc) => {
          user = userDoc.user
          payload.userData.user_id = user.uid
          // The request below carries the new account's token through the request
          // interceptor. The setAuthHeaders copy below predates the interceptor and is
          // never sent: the interceptor sets or removes the header on every request.
          return user.getIdToken()
        })
        .then(token => {
          commit('setAuthHeaders', {token: token})
          // Rails creates the users row and the account holder's exhibitor.
          return state.railsAPI.post('/web/users', signupBody(payload.userData))
            .catch(error => {
              // Rails rejected the form and saved nothing: remove the new login so
              // they can fix the form and sign up again with the same email.
              if (error.response && error.response.status === 422) {
                return user.delete()
                  .catch(() => {})
                  .then(() => Promise.reject(signupError(error.response.data.error)))
              }
              // Anything else keeps the login: Rails may have saved the account.
              return Promise.reject(signupError("Your login was created, but we couldn't finish setting up your account. Try signing in."))
            })
        })
        // The auth listener loaded the profile as soon as Firebase signed the new
        // login in, before Rails had saved it, so load it again now that it exists.
        .then(() => dispatch('loadUserData', {uid: user.uid}).catch(() => {}))
        .then(() => {
          return user.updateProfile({
            displayName: payload.userData.first_name,
            phoneNumber: payload.userData.phone_number
          })
        })
        .then(() => {
          user.sendEmailVerification()
        })
    },
    updateUser: ({state, commit}, payload) => {
      commit('setUser', {user: payload})
      // railsAPI always acts as the signed-in staff account, so while
      // impersonating keep using the Express route, which acts as the
      // impersonated user.
      if (state.impersonatedUid) {
        return state.apiInstance.put(`/data/users/${payload.user_id}`, payload)
      }
      // Rails stores the phone normalized, so keep what it saved.
      return state.railsAPI.patch(`/web/users/${payload.user_id}`, profileBody(payload))
        .then(response => commit('setUser', {user: {...payload, ...response.data.user}}))
    },
    updateEmail: ({state, commit, dispatch}, payload) => {
      // The Firebase login is the staff member's own while impersonating.
      if (state.impersonatedUid) {
        return Promise.reject(new Error("Email changes aren't available while impersonating."))
      }
      let currentUser = auth.currentUser
      // The login's own email, which is already the new one if an earlier attempt
      // changed Firebase but failed after.
      const credential = firebaseInit.EmailAuthProvider.credential(currentUser.email, payload.password)

      return currentUser.reauthenticateWithCredential(credential).then(() => {
        return currentUser.updateEmail(payload.email)
          // Rails takes the email from the login's token, so send a fresh one
          // that carries the new address.
          .then(() => currentUser.getIdToken(true))
          .then(token => {
            commit('setAuthHeaders', {token: token})
            return state.railsAPI.patch(`/web/users/${payload.user_id}`, {})
              .then(response => {
                dispatch('loadUserData', {uid: payload.user_id})
                state.apiInstance.post('/updateUserEmailExternal', {user_id: payload.user_id, email: payload.email})
                if (payload.stripe_customer_id) {
                  state.apiInstance.post('/stripe/updateEmail', payload)
                }
              })
          })
          .catch((error) => {
            throw error
          })
      }).catch((error) => {
        throw error
      })
    },
    updatePassword: ({state}, payload) => {
      const credential = firebaseInit.EmailAuthProvider.credential(payload.email, payload.password)
      let currentUser = auth.currentUser

      return currentUser.reauthenticateWithCredential(credential).then(() => {
        return currentUser.updatePassword(payload.new_password)
          .catch((error) => {
            throw error
          })
      }).catch((error) => {
        throw error
      })
    },
    refreshToken: ({commit}, payload) => {
      if (payload.user) {
        payload.user.getIdToken(true)
          .then(token => {
            commit('setAuthHeaders', {token: token})
          })
      }
    },
    logUserOut: ({commit}) => {
      // Drop the admin app's PowerSync local database (the signed-in user's synced rows)
      // with the session. Runs alongside sign-out; it never rejects.
      clearAdminPowerSync()
      commit('clearImpersonation')
      commit('setImpersonationHeader', null)
      commit('setUser', {user: null})
      commit('setUserData', {userData: null})
      commit('resetEventSurvey')
      return auth.signOut()
    },
    logUserIn: ({state}, payload) => {
      return auth.signInWithEmailAndPassword(payload.email, payload.password)
    },
    forgotPassword: ({state}, payload) => {
      return auth.sendPasswordResetEmail(payload.email)
    },
    loadUserData: ({commit, state}, payload) => {
      return state.apiInstance.get(`/callFunction/userData?userID=${payload.uid}`)
        .then(response => {
          if (response.data.data) {
            commit('setUser', {user: response.data.data.user})
            commit('setUserData', {userData: response.data.data})
            return 'good'
          } else {
            // Return promise error
            commit('setUser', {user: null})
            commit('setUserData', {userData: null})
            return 'error'
          }
        })
    },

    // After an add fails because the account already has an exhibitor by that name, our
    // copy of the list was stale: reload it and return that exhibitor (undefined if not found).
    reloadAndFindExhibitor: ({state, dispatch, getters}, {firstName, lastName}) => {
      return dispatch('loadUserData', {uid: getters.getUserID})
        .then(() => findSavedExhibitorByName(state.userData && state.userData.exhibitors, firstName, lastName))
    },

    // Impersonation Actions
    startUserImpersonation: ({commit, dispatch, state, getters}, payload) => {
      // Check if current user is a super admin
      const currentUserId = getters.getUserID
      if (!state.superAdminUserIds.includes(currentUserId)) {
        return Promise.reject(new Error('Unauthorized: Only super admins can impersonate users'))
      }

      // Prevent impersonating while already impersonating
      if (state.isImpersonating) {
        return Promise.reject(new Error('Already impersonating a user. Stop current impersonation first.'))
      }

      // Accepts an email address or a Firebase UID
      const input = (payload.email || payload.targetUserId || '').trim()
      if (!input) {
        return Promise.reject(new Error('Enter an email address or user ID to impersonate'))
      }

      const resolveTargetUid = input.includes('@')
        ? lookupUserAccount(state.railsAPI, input).then(account => account.uid)
        : Promise.resolve(input)

      // Load the target user's data as the support user, then switch over
      return resolveTargetUid.then(targetUserId => {
        return state.apiInstance.get(`/callFunction/userData?userID=${targetUserId}`)
          .then(response => {
            if (response.data.data) {
              commit('startImpersonation', {
                user: response.data.data.user,
                userData: response.data.data
              })
              // From here on the API sees the impersonated user, not the support account
              commit('setImpersonationHeader', targetUserId)
              return response.data.data
            } else {
              return Promise.reject(new Error('User not found'))
            }
          })
      })
    },

    stopUserImpersonation: ({commit, state}) => {
      if (!state.isImpersonating) {
        return Promise.reject(new Error('Not currently impersonating any user'))
      }
      commit('stopImpersonation')
      commit('setImpersonationHeader', null)
      return Promise.resolve(state.user)
    },

    initializeImpersonationState: ({commit, state}) => {
      // Restore impersonation state from localStorage if it exists
      commit('restoreImpersonationFromStorage')
      // The axios instance is rebuilt on every page load, so the header has to be
      // re-applied or a reload would silently drop back to support permissions.
      if (state.isImpersonating && state.impersonatedUid) {
        commit('setImpersonationHeader', state.impersonatedUid)
      }
      return state.impersonatedUid
    },

    // Exhibitor Actions
    updateExhibitorAnimalStatus: ({commit, state}, payload) => {
      // Update Animal Details
      state.apiInstance.put(`/data/animals/${payload.animal.animal_id}`, {status: payload.animal.status})
        .then(() => {
          commit('updateUserAnimalList', payload.animal)
        })
    },
    updateAnimalDetails: ({commit, state}, payload) => {
      // Update Animal Details
      return state.apiInstance.put(`/data/animals/${payload.animal.animal_id}`, {animal_details: payload.animal.animal_details})
        .then(() => {
          commit('updateUserAnimalList', payload.animal)
        })
    },
    createNewAnimal: ({commit, state}, payload) => {
      return state.apiInstance.post(`/data/animals/`, payload.animal)
        .then((response) => {
          payload.animal.animal_id = response.data.data.animal_id
          commit('addUserAnimalList', payload.animal)
        })
    },
    // General Application
    loadAppData: ({commit, state}) => {
      state.apiInstance.get('/callFunction/showman')
        .then(response => {
          if (response.data.data) {
            commit('setUpcomingEvents', {events: response.data.data.upcoming})
            commit('setShowSeries', {events: response.data.data.show_series})
            commit('setRecentEvents', {events: response.data.data.recent_events})
            commit('setAllEvents', {events: response.data.data.events})
            // commit('setJudges', {judges: response.data.data.judges})
            commit('appInitiated')
            commit('setEventEvents', {events: response.data.data.eventEvents})
            commit('setShowTypes', {showTypes: response.data.data.show_types})
          }
        })
    },

    // Generic User Actions
    addUserData: ({commit, state}, payload) => {
      payload.idName = payload.name.slice(0, -1) + '_id'

      payload.created_by = state.user.user_id
      // payload.user_id = state.user.user_id
      commit('userDataAddData', payload)
      return state.apiInstance.post(`/data/${payload.name}/`, payload)
        .then(response => {
          if (response.status < 400) {
            payload.id = response.data.data[payload.idName]
            commit('userDataSetNewDataID', payload)
            return response
          } else {
            commit('userDataRemoveNewData', payload)
            return Promise.reject(new Error('Data not added'))
          }
        })
        .catch(error => {
          commit('userDataRemoveNewData', payload)
          return Promise.reject(error)
        })
    },
    updateUserData: ({commit, state}, payload) => {
      payload.idName = payload.name.slice(0, -1) + '_id'
      return state.apiInstance.put(`/data/${payload.name}/${payload[payload.idName]}`, payload)
        .then(response => {
          if (response.status === 200) {
            commit('userDataUpdateData', payload)
          } else {
            return Promise.reject(new Error('Update Failed'))
          }
        })
    },
    deleteUserData: ({commit, state}, payload) => {
      payload.idName = payload.name.slice(0, -1) + '_id'

      return state.apiInstance.delete(`/data/${payload.name}/${[payload[payload.idName]]}`)
        .then(response => {
          if (response.status === 200) {
            commit('userDataRemoveData', payload)
          } else {
            return Promise.reject(new Error('Data not deleted'))
          }
        })
    },

    // Stripe Session
    createStripeSession: ({commit, state}, payload) => {
      return state.apiInstance.post('/stripe/createCheckoutSession', payload)
        .then(response => {
          commit('setStripeSession', response.data.data)
          return response.data.data
        })
    },
   
    createStripeSession_redirect: ({commit, state}, payload) => {
      // Returned so callers can handle a rejection (e.g. the API's 409 ALREADY_PAID
      // guard); without it the button silently does nothing on any error.
      return state.apiInstance.post('/stripe/createCheckoutSession', payload)
        .then(response => {
          commit('setStripeSession', response.data.data)
          window.location.href = response.data.data.url
        })
    },
    createStripeSession_redirect_oldSponsor: ({commit, state}, payload) => {
      state.apiInstance.post('/stripe/createCheckoutSessionSeriesSponsor', payload)
        .then(response => {
          commit('setStripeSession', response.data.data)
          window.location.href = response.data.data.url
        })
    },
    // Judges
    addJudge: ({commit, state}, payload) => {
      return state.apiInstance.post('/data/judges', payload)
        .then(response => {
          if (response.status === 200) {
            payload.judge_id = response.data.data.judge_id
            commit('addJudgeToList', payload)
            return payload
          } else {
            return Promise.reject(new Error('Judge not added.'))
          }
        })
        .catch(error => {
          return Promise.reject(error)
        })
    },
    updateJudge: ({commit, state}, payload) => {
      return state.apiInstance.put(`/data/judges/${payload.judge_id}`, payload)
        .then(response => {
          if (response.status === 200) {
            commit('updateJudgeList', payload)
          } else {
            return Promise.reject(new Error('Judge not updated.'))
          }
        })
        .catch(error => {
          return Promise.reject(error)
        })
    },
    // Show Templates
    addShowTemplate: ({commit, state}, payload) => {
      return state.apiInstance.post('/data/showTemplates', payload)
        .then(response => {
          if (response.status === 200) {
            payload.show_template_id = response.data.data.show_template_id
            // commit('addJudgeToList', payload)
            return payload
          } else {
            return Promise.reject(new Error('Template not added.'))
          }
        })
        .catch(error => {
          return Promise.reject(error)
        })
    },
    updateShowTemplate: ({commit, state}, payload) => {
      return state.apiInstance.put(`/data/showTemplates/${payload.show_template_id}`, payload)
        .then(response => {
          if (response.status === 200) {
            // commit('updateJudgeList', payload)
          } else {
            return Promise.reject(new Error('Template not updated.'))
          }
        })
        .catch(error => {
          return Promise.reject(error)
        })
    },
    deleteShowTemplate: ({commit, state}, payload) => {
      return state.apiInstance.delete(`/data/showTemplates/${payload.show_template_id}`)
        .then(response => {
          if (response.status === 200) {
            commit('removeShowTemplate', payload)
            return payload
          } else {
            return Promise.reject(new Error('Template not deleted.'))
          }
        })
    },
    setSearchParam: ({commit}, payload) => {
      switch (payload.field) {
        case 'species':
          commit('setSpecies', payload.value)
          break
        case 'location':
          commit('setLocation', payload.value)
          break
        case 'radius':
          commit('setRadius', payload.value)
          break
        case 'drivingTime':
          commit('setDrivingTime', payload.value)
          break
        case 'distanceType':
          commit('setDistanceType', payload.value)
          break
        case 'startDate':
          commit('setStartDate', payload.value)
          break
        case 'endDate':
          commit('setEndDate', payload.value)
          break
        case 'name':
          commit('setName', payload.value)
          break
      }
    },
    setShowMap: ({commit}, payload) => {
      commit('setShowMap', payload)
    },
    setMapBounds: ({commit}, payload) => {
      commit('setMapBounds', payload)
    }
  },
  getters: {
    judgeSelectList: state => {
      return state.judges.map(judge => {
        return {
          id: judge.judge_id,
          name: `${judge.last_name}, ${judge.first_name}`
        }
      })
    },
    getUserID: state => {
      if (!state.user) {
        return null
      } else if (state.user.uid) {
        return state.user.uid
      } else {
        return state.user.user_id
      }
    },
    // Flatten the year-grouped events the user is entered in (state.userData.events)
    // into upcoming/past lists for the account dropdown and profile.
    upcomingEnteredShows: state => {
      if (!state.userData || !state.userData.events) {
        return []
      }
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const shows = []
      state.userData.events.forEach(yearGroup => {
        (yearGroup.events || []).forEach(e => {
          if (e.start_date && new Date(e.start_date) >= today) {
            shows.push(e)
          }
        })
      })
      return shows.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
    },
    pastEnteredShows: state => {
      if (!state.userData || !state.userData.events) {
        return []
      }
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const shows = []
      state.userData.events.forEach(yearGroup => {
        (yearGroup.events || []).forEach(e => {
          if (!e.start_date || new Date(e.start_date) < today) {
            shows.push(e)
          }
        })
      })
      return shows.sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
    },
    apiInstanceAuthHeader: state => {
      return state.apiInstance.defaults.headers.common.Authorization
    },
    // Impersonation getters
    isImpersonating: state => {
      return state.isImpersonating
    },
    isSuperAdmin: (state, getters) => {
      const currentUserId = getters.getUserID
      return state.superAdminUserIds.includes(currentUserId)
    },
    originalAdminUser: state => {
      return state.originalAdminUser
    },
    impersonationStatus: state => {
      if (!state.isImpersonating) {
        return null
      }
      return {
        isImpersonating: true,
        impersonatedUser: state.user,
        originalAdmin: state.originalAdminUser
      }
    },
    filteredUpcomingShows: (state, getters) => {
      let list = state.upcomingEvents
      
      // Filter by state when map is hidden and location type is 'state'
      if (!state.showMap && state.searchParams.location && state.searchParams.location.type === 'state') {
        // For state filtering, match by state abbreviation or full name
        const stateName = state.searchParams.location.name
        const stateAbbr = state.searchParams.location.state_info?.abbreviation
        
        list = list.filter(s => {
          if (s.location_details && s.location_details.state) {
            const showState = s.location_details.state
            // Match by full state name or abbreviation
            return showState === stateName || 
                   showState === stateAbbr ||
                   showState.toLowerCase() === stateName.toLowerCase()
          }
          return false
        })
      }
      // Filter by location and radius (apply when location and radius are set, but not for state filtering)
      else if (state.searchParams.location && state.searchParams.radius && state.searchParams.location.type !== 'state') {
        list = list.filter(s => {
          const showLocation = getters.getShowCoordinates(s)
          if (showLocation) {
            let centerLat, centerLng
            if (state.searchParams.location.coordinates) {
              centerLat = state.searchParams.location.coordinates.lat
              centerLng = state.searchParams.location.coordinates.lng
            } else {
              centerLat = state.searchParams.location.lat
              centerLng = state.searchParams.location.lng
            }
            
            const distance = getters.calculateDistance(
              centerLat,
              centerLng,
              showLocation.lat,
              showLocation.lng
            )
            return distance <= state.searchParams.radius
          }
          return false
        })
      }
      
      // Filter by date range
      if (state.searchParams.startDate || state.searchParams.endDate) {
        list = list.filter(s => {
          if (!s.start_date) return false
          
          const showDate = new Date(s.start_date)
          const startDate = state.searchParams.startDate ? new Date(state.searchParams.startDate) : null
          const endDate = state.searchParams.endDate ? new Date(state.searchParams.endDate) : null
          
          // Check if show date is within the specified range
          if (startDate && showDate < startDate) return false
          if (endDate && showDate > endDate) return false
          
          return true
        })
      }
      
      // Filter by species
      if (state.searchParams.species && Array.isArray(state.searchParams.species) && state.searchParams.species.length > 0) {
        list = list.filter(s => {
          if (s.species) {
            // Check if the event has ANY of the selected species
            return state.searchParams.species.some(selectedSpecies => 
              s.species.some(eventSpecies => eventSpecies.species === selectedSpecies)
            )
          } else {
            return false
          }
        })
      }
      
      return list
    },
    filteredRecentShows: (state, getters) => {
      let list = state.recentEvents
      
      // Filter by species
      if (state.searchParams.species && Array.isArray(state.searchParams.species) && state.searchParams.species.length > 0) {
        list = list.filter(s => {
          if (s.species) {
            // Check if the event has ANY of the selected species
            return state.searchParams.species.some(selectedSpecies => 
              s.species.some(eventSpecies => eventSpecies.species === selectedSpecies)
            )
          } else {
            return false
          }
        })
      }
      
      return list
    },
    filteredShowsByMapBounds: (state, getters) => {
      // Start with already filtered shows (includes location/radius filtering)
      let filtered = getters.filteredUpcomingShows
      
      // Only apply map bounds filtering if bounds are set
      if (!state.mapBounds) {
        return filtered // Return all filtered shows if no bounds set
      }
      
      const boundsFiltered = filtered.filter(show => {
        const showLocation = getters.getShowCoordinates(show)
        if (!showLocation) return false

        // Check if show location is within map bounds
        const withinBounds = showLocation.lat >= state.mapBounds.south &&
               showLocation.lat <= state.mapBounds.north &&
               showLocation.lng >= state.mapBounds.west &&
               showLocation.lng <= state.mapBounds.east
               
        return withinBounds
      })
      
      return boundsFiltered
    },
    searchResults: (state, getters) => {
      if (!state.searchParams.name || state.searchParams.name === '') {
        return []
      }
      
      // State abbreviation to full name mapping
      const stateMapping = {
        'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
        'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
        'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
        'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
        'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
        'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
        'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
        'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
        'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
        'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
      }
      
      // Create reverse mapping (full name to abbreviation)
      const reverseStateMapping = {}
      Object.keys(stateMapping).forEach(abbr => {
        reverseStateMapping[stateMapping[abbr].toLowerCase()] = abbr
      })
      
      let allEvents = getters.filteredRecentShows.concat(getters.filteredUpcomingShows)
      let value = state.searchParams.name.trim().replace(/\\+/g, '')
      const regex = new RegExp(value, 'gi')
      
      return allEvents.filter(e => {
        // Search by name
        const nameMatch = e.name.match(regex)
        
        // Search by city
        const cityMatch = e.location_details && e.location_details.city && e.location_details.city.match(regex)
        
        // Enhanced state search - check both abbreviation and full name
        let stateMatch = false
        if (e.location_details && e.location_details.state) {
          const showState = e.location_details.state
          
          // Direct match with the stored state value (could be abbreviation or full name)
          stateMatch = showState.match(regex)
          
          // If no direct match, check if search term is a state name and show state is abbreviation
          if (!stateMatch) {
            const searchTermLower = value.toLowerCase()
            const stateAbbr = reverseStateMapping[searchTermLower]
            if (stateAbbr && showState === stateAbbr) {
              stateMatch = true
            }
          }
          
          // If no match yet, check if search term is abbreviation and show state is full name
          if (!stateMatch) {
            const searchTermUpper = value.toUpperCase()
            const fullStateName = stateMapping[searchTermUpper]
            if (fullStateName && showState.toLowerCase() === fullStateName.toLowerCase()) {
              stateMatch = true
            }
          }
        }
        
        return nameMatch || cityMatch || stateMatch
      })
    },
    // Helper function to get show coordinates
    getShowCoordinates: () => (show) => {
      // Use actual geolocation data from the event
      if (show.location_details && show.location_details.geolocation) {
        return {
          lat: show.location_details.geolocation.latitude,
          lng: show.location_details.geolocation.longitude
        }
      }
      
      return null
    },
    // Helper function to calculate distance between two points using Haversine formula
    calculateDistance: () => (lat1, lng1, lat2, lng2) => {
      const R = 3959 // Radius of the Earth in miles
      const dLat = (lat2 - lat1) * Math.PI / 180
      const dLng = (lng2 - lng1) * Math.PI / 180
      const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c // Distance in miles
    }
  }
})

// Rails' /admin endpoints scope every request to the Showman-Event-Id event.
// Read at send time so it's always the admin event that's open now; a request
// that names its own event keeps it.
store.state.railsAPI.interceptors.request.use(config => {
  const event = store.state.admin.adminData.event
  if (event && config.url.startsWith('/admin/') && !config.headers['Showman-Event-Id']) {
    config.headers['Showman-Event-Id'] = event.event_id
  }
  return config
})

// The signed-in person's Firebase token, read per request, warmed when the tab comes
// back, and replayed once on a 401 (see utils/authToken.js). setAuthHeaders still
// writes a copy into the defaults, but this sets or removes the header on every
// request, so that copy is never what gets sent.
const tokens = createTokenSource(firebaseInit.auth)
installAuthInterceptors(store.state.apiInstance, 'Authorization', tokens)
installAuthInterceptors(store.state.railsAPI, 'Firebase-Token', tokens)
refreshOnReturn(tokens)

export default store
